import { useState, useEffect } from 'react';
import { Play, Search, Dumbbell } from 'lucide-react';
import { Input } from '../ui/input';
import ExerciseDetail from './ExerciseDetail';

const ExerciseLibrary = ({ api, token, user }) => {
  const [groups, setGroups] = useState([]);      // [{ muscle_group, count }]
  const [active, setActive] = useState('All');
  const [query, setQuery] = useState('');
  const [exercises, setExercises] = useState([]);
  const [selected, setSelected] = useState(null); // exercise for the video modal
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);  // bump to refetch after an admin edit

  // Load group counts (re-runs after an admin recategorizes an exercise)
  useEffect(() => {
    api.get('/exercises/groups', token).then(setGroups).catch(() => setGroups([]));
  }, [api, token, reloadKey]);

  // Load exercises whenever the filter or (debounced) search changes
  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      const params = new URLSearchParams();
      if (active !== 'All') params.set('group', active);
      if (query.trim()) params.set('q', query.trim());
      api.get(`/exercises${params.toString() ? `?${params}` : ''}`, token)
        .then(setExercises)
        .catch(() => setExercises([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [api, token, active, query, reloadKey]);

  const total = groups.reduce((n, g) => n + Number(g.count), 0);

  // Detail view (video + PRs + history) when an exercise is selected
  if (selected) {
    return <ExerciseDetail api={api} token={token} user={user} exercise={selected} onBack={() => setSelected(null)} onUpdated={() => setReloadKey((k) => k + 1)} />;
  }

  return (
    <div className="max-w-5xl mx-auto p-4">
      <h2 className="font-display font-bold text-2xl mb-4">Exercise Library</h2>

      {/* Search */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder="Search exercises…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {/* Group filter */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
        <Chip label="All" count={total} active={active === 'All'} onClick={() => setActive('All')} />
        {groups.map((g) => (
          <Chip
            key={g.muscle_group}
            label={g.muscle_group}
            count={Number(g.count)}
            active={active === g.muscle_group}
            onClick={() => setActive(g.muscle_group)}
          />
        ))}
      </div>

      {/* Results */}
      {loading ? (
        <p className="text-muted-foreground text-sm py-12 text-center">Loading…</p>
      ) : exercises.length === 0 ? (
        <EmptyState libraryEmpty={total === 0} isAdmin={user.role === 'admin'} />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {exercises.map((ex) => (
            <button
              key={ex.id}
              onClick={() => setSelected(ex)}
              className="group text-left rounded-lg border bg-card overflow-hidden hover:border-primary transition-colors"
            >
              <div className="relative aspect-video bg-muted">
                {ex.youtube_video_id ? (
                  <>
                    <img
                      src={`https://i.ytimg.com/vi/${ex.youtube_video_id}/mqdefault.jpg`}
                      alt={ex.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
                      <Play className="h-9 w-9 text-white drop-shadow" fill="white" />
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground/40">
                    <Dumbbell className="h-8 w-8" />
                  </div>
                )}
              </div>
              <div className="p-2.5">
                <div className="text-sm font-medium leading-snug line-clamp-2">{ex.name}</div>
                <div className="text-xs text-primary mt-1">{ex.muscle_group}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Chip = ({ label, count, active, onClick }) => (
  <button
    onClick={onClick}
    className={`flex-none rounded-full px-3 py-1.5 text-sm border transition-colors ${
      active ? 'bg-primary text-primary-foreground border-primary' : 'bg-card hover:bg-accent border-input'
    }`}
  >
    {label} <span className={active ? 'opacity-80' : 'text-muted-foreground'}>{count}</span>
  </button>
);

const EmptyState = ({ libraryEmpty, isAdmin }) => (
  <div className="text-center py-16 text-muted-foreground">
    <Dumbbell className="h-10 w-10 mx-auto mb-3 opacity-40" />
    {libraryEmpty ? (
      <>
        <p>The exercise library is empty.</p>
        {isAdmin && <p className="text-sm mt-1">Run <code className="text-primary">npm run ingest</code> on the server to import the PE exercise videos.</p>}
      </>
    ) : (
      <p>No exercises match your search.</p>
    )}
  </div>
);

export default ExerciseLibrary;
